import { useEffect } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { VolumeSlider } from "../editor/VolumeSlider";
import { useGlimpsedFiles } from "../../store/filteredFiles/hooks";
import { useModals } from "../../store/dialogs/hooks";
import {
  PauseCircle,
  PlayCircle,
  SkipNext,
  SkipPrevious,
} from "@mui/icons-material";
import backSixSeconds from "../../svg/backSixSeconds.svg";
import forwardSixSeconds from "../../svg/forwardSixSeconds.svg";

export default function GlimpsedFileAudioControl({
  clipDuration,
  hidePreviousNext,
  onJumpBack,
  onJumpForward,
}) {
  const {
    setPlayStateFilteredFiles,
    setVolumeFilteredFiles,
    playState,
    clipTime,
  } = useGlimpsedFiles();
  const { showClipDetailsDialog } = useModals();

  //set icon to play or pause, toggle when space bar is pressed
  useEffect(() => {
    // when component mounts
    const handleSpaceBarPress = (event) => {
      if (!showClipDetailsDialog) {
        if (event.code === "Space") {
          event.preventDefault();

          // when space bar is pressed set redux play state to play or pause
          const newPlayState = playState === "PLAY" ? "PAUSE" : "PLAY";
          setPlayStateFilteredFiles(newPlayState);
        }
      }
    };

    document.addEventListener("keydown", handleSpaceBarPress);
    return () => {
      //when component unmounts
      document.removeEventListener("keydown", handleSpaceBarPress);
    };
  }, [playState, showClipDetailsDialog, setPlayStateFilteredFiles]);

  return (
    <Box
      display="grid"
      gap={2}
      gridTemplateColumns="repeat(3, 1fr)"
      px={2}
      width={1}
    >
      <Box></Box>
      <Box display="flex" alignItems="center" justifyContent="center">
        {!hidePreviousNext && (
          <Tooltip title="Previous">
            <IconButton
              aria-label="skip previous"
              color="primary"
              onClick={() => {
                if (onJumpBack) onJumpBack(0);
              }}
              size="small"
            >
              <SkipPrevious />
            </IconButton>
          </Tooltip>
        )}
        {!hidePreviousNext && (
          <Tooltip title="Back six seconds">
            <IconButton
              aria-label="back six seconds"
              onClick={() => {
                if (onJumpBack) onJumpBack(clipTime - 6);
              }}
              size="small"
            >
              <img
                src={backSixSeconds}
                height={24}
                width={24}
                alt="back six seconds"
              />
            </IconButton>
          </Tooltip>
        )}
        {playState !== "PLAY" && (
          <Tooltip title="Play">
            <IconButton
              aria-label="play"
              color="primary"
              size="small"
              sx={{ p: 0 }}
              onClick={() => setPlayStateFilteredFiles("PLAY")}
            >
              <PlayCircle sx={{ fontSize: 50 }} />
            </IconButton>
          </Tooltip>
        )}
        {playState === "PLAY" && (
          <Tooltip title="Pause">
            <IconButton
              aria-label="pause"
              color="primary"
              size="small"
              sx={{ p: 0 }}
              onClick={() => setPlayStateFilteredFiles("PAUSE")}
            >
              <PauseCircle sx={{ fontSize: 50 }} />
            </IconButton>
          </Tooltip>
        )}
        {!hidePreviousNext && (
          <Tooltip title="Forward six seconds">
            <IconButton
              aria-label="forward six seconds"
              onClick={() => {
                if (onJumpForward) {
                  const newValue = clipTime + 6 > clipDuration + 0.15;
                  if (newValue) {
                    setPlayStateFilteredFiles("PAUSE");
                    onJumpForward(clipDuration + 0.15);
                  } else {
                    onJumpForward(clipTime + 6);
                  }
                }
              }}
              size="small"
            >
              <img
                src={forwardSixSeconds}
                height={24}
                width={24}
                alt="forward six seconds"
              />
            </IconButton>
          </Tooltip>
        )}
        {!hidePreviousNext && (
          <Tooltip title="Next">
            <IconButton
              aria-label="skip next"
              color="primary"
              onClick={() => {
                if (onJumpForward) {
                  setPlayStateFilteredFiles("PAUSE");
                  onJumpForward(clipDuration + 0.15);
                }
              }}
              size="small"
            >
              <SkipNext />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box display="flex" alignItems="center" justifyContent="flex-end">
        <VolumeSlider value={100} onChange={(v) => setVolumeFilteredFiles(v)} />
      </Box>
    </Box>
  );
}
